import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { displayView } from 'constants/UI';
import { setGridView, setProductGroup } from 'actions/UI';
import { useMeasureListHeight } from 'containers/Col2/GridViews/util';
import { getQuickSelectProductIDs } from 'reducers/PointsOfSale';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import useProducts from 'utils/hooks/useProducts';
import { getSetting } from 'reducers/configs/settings';

import Products from '../Products';

import { homeBttn, leftArrowBttn, rightArrowBttn } from './SpecialBttns';

type Props = {
  isMobileView?: boolean;
};

const QuickSelectView: React.FC<Props> = ({ isMobileView }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [nrItems, setNrItems] = useState(0);
  const quickSelectProductIDs = useSelector(getQuickSelectProductIDs);
  const getItemsFromFirstPriceListOnly = useSelector(
    getSetting('pos_allow_selling_only_from_pricelist1'),
  );
  const warehouseID = useSelector(getSelectedWarehouseID);
  const { products } = useProducts({
    pageNo: page + 1,
    recordsOnPage: nrItems,
    getProductsFor: 'SALES',
    warehouseID,
    getWarehouseSpecificVAT: 1,
    quickPosProducts: 1,
    getItemsFromFirstPriceListOnly,
  });

  const goToHomePage = useCallback(() => {
    dispatch(setGridView(displayView.main));
    dispatch(setProductGroup('0'));
  }, [dispatch]);

  const openPreviousPage = () => {
    setPage(prevPage => (prevPage > 0 ? prevPage - 1 : prevPage));
  };

  const openNextPage = () => {
    setPage(prevPage => prevPage + 1);
  };

  const startBttns = useMemo(() => [homeBttn(goToHomePage)], [goToHomePage]);
  const endBttns = useMemo(
    () =>
      nrItems + 2 < quickSelectProductIDs.length
        ? [
            leftArrowBttn(openPreviousPage, page === 0),
            rightArrowBttn(
              openNextPage,
              (page + 1) * nrItems >= quickSelectProductIDs.length,
            ),
          ]
        : [],
    [nrItems, quickSelectProductIDs.length, page],
  );

  const onSetNrLines = useCallback((lines, items) => setNrItems(items), []);

  const { needsToMeasure, Measure, totalLines } = useMeasureListHeight();
  const lines = Math.max(7, totalLines ?? 0);
  if (needsToMeasure) return Measure;

  return (
    <Products
      products={products}
      specialBttnsStart={isMobileView ? [] : startBttns}
      specialBttnsEnd={endBttns}
      onSetNrLines={onSetNrLines}
      minLines={lines}
      maxLines={lines}
    />
  );
};

export default QuickSelectView;
